import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { getApiLanguage } from "src/services/commonLanguageHeader";
import {
  createWarehouseUnit,
  deleteWarehouseUnit,
  getWarehouseUnitHwStatusLookup,
  getWarehouseUnitOperationTypeLookup,
  getWarehouseUnitsList,
  updateWarehouseUnit,
  updateWarehouseUnitOperationType,
  type WarehouseUnitCreatePayload,
  type WarehouseUnitListParams,
} from "src/services/warehouseUnitService";
import { QueryNames } from "./queries.constant";
import { useGeneralRefetchOverride } from "./general.queries";

export const useQueryGetWarehouseUnitsList = (
  params: WarehouseUnitListParams = {},
  options: { enabled?: boolean } & Record<string, unknown> = {},
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useGeneralRefetchOverride(
    useQuery({
      queryKey: [...QueryNames.warehouseUnitList, params, language],
      queryFn: () => getWarehouseUnitsList(params, language),
      refetchOnMount: "always",
      retry: false,
      ...options,
    }),
    options.enabled !== false,
  );
};

export const useMutationDeleteWarehouseUnit = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (id: string | number) => deleteWarehouseUnit(id, language),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryNames.warehouseUnitList });
    },
  });
};

export const useQueryGetWarehouseUnitOperationTypeLookup = (
  options: { enabled?: boolean } & Record<string, unknown> = {},
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useQuery({
    queryKey: [...QueryNames.warehouseUnitOperationTypeLookup, language],
    queryFn: () => getWarehouseUnitOperationTypeLookup(language),
    retry: false,
    ...options,
  });
};

export const useMutationUpdateWarehouseUnitOperationType = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: ({
      id,
      operationTypeId,
    }: {
      id: string | number;
      operationTypeId: string | number;
    }) => updateWarehouseUnitOperationType(id, { operationTypeId }, language),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryNames.warehouseUnitList });
    },
  });
};

export const useQueryGetWarehouseUnitHwStatusLookup = (
  options: { enabled?: boolean } & Record<string, unknown> = {},
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useQuery({
    queryKey: [...QueryNames.warehouseUnitHwStatusLookup, language],
    queryFn: () => getWarehouseUnitHwStatusLookup(language),
    retry: false,
    ...options,
  });
};

export const useMutationCreateWarehouseUnit = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (payload: WarehouseUnitCreatePayload) => createWarehouseUnit(payload, language),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryNames.warehouseUnitList });
    },
  });
};

export const useMutationUpdateWarehouseUnit = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string | number;
      payload: Partial<WarehouseUnitCreatePayload>;
    }) => updateWarehouseUnit(id, payload, language),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryNames.warehouseUnitList });
    },
  });
};
